import {
  EntityType,
  GetTaskByIdDocument,
  useUpdateAndCreateSubtasksMutation,
  WorkflowCode,
} from '@exam-management/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  type DialogProps,
} from '@shared/components';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import { useForm, FormProvider } from 'react-hook-form';

import { useQSetReviewTask } from '../../providers';
import { QSetReviewFormUserSelection } from '../qset-review-form-user-selection';

interface AddReviewerFormValues {
  reviewer: { label: string; value: string } | null;
}

export interface AddReviewerModalProps extends Omit<DialogProps, 'onSubmit' | 'open'> {
  onClose: () => void;
  open?: boolean;
  excludeUserIds?: string[];
  qsetId: string;
}

export default function AddReviewerModal(props: Readonly<AddReviewerModalProps>) {
  const { onClose, open = false, excludeUserIds = [], qsetId } = props;

  const { mainTaskDetail } = useQSetReviewTask();
  const { showSnackbar } = useSnackbar();
  const logger = useLogger();

  const [updateAndCreateSubtasks, { loading: isSubmitting }] = useUpdateAndCreateSubtasksMutation({
    onCompleted: () => {
      showSnackbar({ message: 'Reviewer has been added', variant: 'success' });
    },
    onError: (error) => {
      logger.error(error);
      showSnackbar({ message: 'Failed to add reviewer', variant: 'error' });
    },
    refetchQueries: [
      {
        query: GetTaskByIdDocument,
        variables: {
          getTaskByIdInput: {
            taskId: mainTaskDetail.id,
          },
        },
      },
    ],
  });

  const methods = useForm<AddReviewerFormValues>({
    defaultValues: {
      reviewer: null,
    },
  });

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (!data.reviewer?.value) return;

    const existingSubtasks = mainTaskDetail.subtasks.map((subtask) => ({
      id: subtask.id,
      assigneeId: subtask.assignee?.id ?? '',
      dueDate: subtask.dueDate ?? new Date(),
      entityType: EntityType.Qset,
      entityId: subtask.qsetId,
    }));

    const newSubtask = {
      assigneeId: data.reviewer.value,
      dueDate: mainTaskDetail.dueDate ?? new Date(),
      entityType: EntityType.Qset,
      entityId: qsetId,
    };

    await updateAndCreateSubtasks({
      variables: {
        updateAndCreateSubtasksInput: {
          mainTaskId: mainTaskDetail.id,
          workflowCode: WorkflowCode.ReviewQSet,
          subtasks: [...existingSubtasks, newSubtask],
        },
      },
    });

    methods.reset();
    onClose();
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <DialogTitle onClose={handleClose}>Add Reviewer</DialogTitle>
      <Divider />
      <DialogContent>
        <FormProvider {...methods}>
          <QSetReviewFormUserSelection name='reviewer' excludeUserIds={excludeUserIds} />
        </FormProvider>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit} loading={isSubmitting}>
          Add Reviewer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
